import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface RaelaSuggestion {
  tone: string;
  text: string;
}

export interface RaelaSuggestionRow {
  id: string;
  suggestions: RaelaSuggestion[];
}

/**
 * RAELA outbound co-pilot — generates 3 reply ideas in different tones
 * for the current draft + recent conversation context. Member can
 * use one (which logs which tone resonates) or ignore them entirely.
 *
 * Surface contract:
 *   - Member sees a small "✦ Co-pilot" button next to the composer.
 *   - On click, fetch returns 3 suggestions; clicking one fills the
 *     composer (handled by caller via onPick).
 *   - Suggestions never auto-send; member always reviews + sends.
 */
export function useRaelaCoPilot() {
  const [suggestions, setSuggestions] = useState<RaelaSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [rowId, setRowId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const generate = useCallback(
    async (args: {
      conversationId: string | null;
      draft: string;
      recentMessages: { role: "me" | "them"; text: string }[];
      app?: "fryqnc" | "pulsynq" | "mindlynq";
    }) => {
      setLoading(true);
      setError(null);
      const { data, error: invokeErr } = await supabase.functions.invoke(
        "raela-suggest",
        {
          body: {
            conversation_id: args.conversationId,
            draft: args.draft,
            recent: args.recentMessages.slice(-8),
            app: args.app ?? "fryqnc",
          },
        },
      );
      setLoading(false);
      if (invokeErr) {
        setError(invokeErr.message);
        return { suggestions: [] as RaelaSuggestion[], error: invokeErr.message };
      }
      const payload = data as {
        suggestion_id: string;
        suggestions: RaelaSuggestion[];
      };
      setRowId(payload?.suggestion_id ?? null);
      setSuggestions(payload?.suggestions ?? []);
      return { suggestions: payload?.suggestions ?? [], error: null };
    },
    [],
  );

  const markUsed = useCallback(
    async (index: number) => {
      if (!rowId) return;
      await supabase
        .from("raela_suggestions")
        .update({ used_index: index, used_at: new Date().toISOString() })
        .eq("id", rowId);
    },
    [rowId],
  );

  const reset = useCallback(() => {
    setSuggestions([]);
    setRowId(null);
    setError(null);
  }, []);

  return { suggestions, loading, error, generate, markUsed, reset };
}
